import { cn } from '@/ui/lib/utils'

export interface QuantReadoutCell {
  label: string
  value: string
  /** `accent` (the close), `up`/`down` (sign colours) or default paper. */
  tone?: 'accent' | 'up' | 'down'
}

export interface QuantReadoutCellsProps {
  cells: QuantReadoutCell[]
  /** Muted text pushed to the right edge (the Manual trade stream endpoint). */
  endpoint?: string
  className?: string
}

const TONE: Record<NonNullable<QuantReadoutCell['tone']>, string> = {
  accent: 'text-accent-line',
  up: 'text-accent-line',
  down: 'text-alert',
}

/** The shared readout row (handoff 3.4 and 4.3): a label/value pair per cell on a hairline-topped strip. */
export function QuantReadoutCells({ cells, endpoint, className }: QuantReadoutCellsProps) {
  return (
    <div
      data-readout
      className={cn('flex shrink-0 flex-wrap gap-x-[22px] gap-y-1 border-t border-paper-14 px-7 pt-2 pb-2.5 font-mono', className)}
    >
      {cells.map((cell, i) => (
        <div key={`${i}-${cell.label}`} className="flex items-baseline gap-1.5">
          <span className="text-[9px] tracking-[0.16em] text-paper-40 uppercase">{cell.label}</span>
          <span data-readout-value={cell.label} className={cn('text-[11.5px]', cell.tone ? TONE[cell.tone] : 'text-paper-90')}>
            {cell.value}
          </span>
        </div>
      ))}
      {endpoint !== undefined && (
        <>
          <span className="flex-1" />
          <span data-readout-endpoint className="text-[10.5px] [overflow-wrap:anywhere] text-paper-40">
            {endpoint}
          </span>
        </>
      )}
    </div>
  )
}

export interface QuantReadoutProps {
  /** 1-based bar number. */
  bar: number
  /** ISO time with a `Z` suffix. */
  time: string
  open: number
  high: number
  low: number
  close: number
  equity?: number | null
  /** `BUY @ p` / `SELL @ p` / `—`. */
  fill: string
  digits?: number
  className?: string
}

/** Bar readout row (spec 036, handoff 3.4): Bar, Time, O, H, L, C (accent), Equity, Fill. */
export function QuantReadout({ bar, time, open, high, low, close, equity, fill, digits = 2, className }: QuantReadoutProps) {
  const f = (v: number): string => v.toFixed(digits)
  const cells: QuantReadoutCell[] = [
    { label: 'Bar', value: String(bar) },
    { label: 'Time', value: time },
    { label: 'O', value: f(open) },
    { label: 'H', value: f(high) },
    { label: 'L', value: f(low) },
    { label: 'C', value: f(close), tone: 'accent' },
    { label: 'Equity', value: equity === null || equity === undefined ? '—' : f(equity) },
    { label: 'Fill', value: fill },
  ]
  return <QuantReadoutCells cells={cells} className={className} />
}
